import { TooltipProvider } from "@/components/ui/tooltip";
import type { Entity } from "@cohesive/ui-components";
import {
  Circle,
  FileText,
  SquareMousePointer,
  StickyNote,
  Type,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { CANVAS_KEYS } from "../canvas-controls.config";
import { useCanvasToolbarLayout } from "../context/CanvasToolbarLayoutContext";
import { useCanvasChromeEnterAnimation } from "../hooks/useCanvasChromeEnterAnimation";
import { useCanvasShapeAndEdgeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import type { ConnectorPreset } from "../hooks/useConnectorPlacement";
import type { ShapeType } from "../nodes/shapes/index";
import { ShapesPopover } from "./ShapesPopover";
import {
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
  useToolbarPopoverCoordinator,
} from "./toolbar";
import { CanvasToolbarFlyoutAnchor } from "./toolbar/CanvasToolbarFlyoutAnchor";
import { SHAPE_LUCIDE_ICON } from "./toolbar/canvasToolbarIcon";
import { CanvasConnectorsMenu } from "./toolbar/primary/CanvasConnectorsMenu";
import type { CanvasInsertMenuActions } from "./toolbar/primary/CanvasInsertMenu";
import { CanvasInsertMenu } from "./toolbar/primary/CanvasInsertMenu";

interface CanvasToolbarProps {
  onToggleTextNoteMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingTextNote: boolean;
  onToggleDocMode: (screenPos?: { x: number; y: number }) => void;
  isPlacingDoc: boolean;
  onToggleTextLabelMode: () => void;
  isPlacingTextLabel: boolean;
  onToggleSectionMode: () => void;
  isPlacingSection: boolean;
  onCancelPlacement?: () => void;
  /** Close toolbar flyouts and add-context without cancelling placement. */
  onDismissToolbarMenus?: () => void;
  isShapesMenuOpen: boolean;
  isPlacingShape: boolean;
  pendingShapeType: ShapeType | null;
  onToggleShapesMenu: () => void;
  onShapeSelect: (shape: ShapeType) => void;
  isConnectorActive: boolean;
  activeConnectorPreset: ConnectorPreset | null;
  onConnectorSelect: (preset: ConnectorPreset) => void;
  isWidgetsMenuOpen: boolean;
  onWidgetsMenuOpenChange: (open: boolean) => void;
  isLinkMenuOpen: boolean;
  onLinkMenuOpenChange: (open: boolean) => void;
  insertMenu: CanvasInsertMenuActions & {
    onIntegrationEntitySelect?: (entity: Entity) => void;
  };
}

function ShortcutChip({ children }: { children: string }) {
  return (
    <kbd className="ml-1 rounded border border-current/40 px-1 py-px font-mono text-[10px] leading-none">
      {children}
    </kbd>
  );
}

export function CanvasToolbar({
  onToggleTextNoteMode,
  isPlacingTextNote,
  onToggleDocMode,
  isPlacingDoc,
  onToggleTextLabelMode,
  isPlacingTextLabel,
  onToggleSectionMode,
  isPlacingSection,
  onCancelPlacement,
  onDismissToolbarMenus,
  isShapesMenuOpen,
  isPlacingShape,
  pendingShapeType,
  onToggleShapesMenu,
  onShapeSelect,
  isConnectorActive,
  activeConnectorPreset,
  onConnectorSelect,
  isWidgetsMenuOpen,
  onWidgetsMenuOpenChange,
  isLinkMenuOpen,
  onLinkMenuOpenChange,
  insertMenu,
}: CanvasToolbarProps) {
  const { orientation, dock, tooltipSide, containerClassName } =
    useCanvasToolbarLayout();
  const { lastPlacedShape } = useCanvasShapeAndEdgeStyle();

  const { open, setOpen, closeAll, toggleMenu } =
    useToolbarPopoverCoordinator();

  const toolbarInsertMenu = useMemo(
    () => ({
      ...insertMenu,
      onAddWidget: (preset: Parameters<typeof insertMenu.onAddWidget>[0]) => {
        closeAll();
        insertMenu.onAddWidget(preset);
      },
      onAddLinkSubmit: (url: string) => {
        closeAll();
        insertMenu.onAddLinkSubmit(url);
      },
      onNewNote: () => {
        closeAll();
        insertMenu.onNewNote();
      },
      onNewDocument: () => {
        closeAll();
        insertMenu.onNewDocument();
      },
      onUploadFile: () => {
        closeAll();
        insertMenu.onUploadFile();
      },
    }),
    [insertMenu, closeAll],
  );

  useEffect(() => {
    if (!isShapesMenuOpen && open === "shape") {
      setOpen(null);
    }
  }, [isShapesMenuOpen, open, setOpen]);

  const labelActive = isPlacingTextLabel;
  const isShapeFlyoutOpen = open === "shape" || isShapesMenuOpen;
  const shapesActive = isShapeFlyoutOpen || isPlacingShape || isConnectorActive;

  // The Shapes trigger wears the last pick (design-system canon: current
  // state on the trigger).
  const wornShape = pendingShapeType ?? lastPlacedShape;
  const ShapesTriggerIcon = wornShape ? SHAPE_LUCIDE_ICON[wornShape] : Circle;

  const handleIntegrationEntitySelect = (entity: Entity) => {
    if (insertMenu.onIntegrationEntitySelect) {
      insertMenu.onIntegrationEntitySelect(entity);
    } else {
      insertMenu.onSelectEntity(entity, { x: 0, y: 0 });
    }
  };

  const shouldAnimateChromeEnter = useCanvasChromeEnterAnimation();

  return (
    <motion.div
      initial={
        shouldAnimateChromeEnter
          ? dock === "left"
            ? { opacity: 0, x: -20 }
            : { opacity: 0, y: 20 }
          : false
      }
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        opacity: { duration: 0.2 },
        x: { duration: 0.2 },
        y: { duration: 0.2 },
      }}
      className={containerClassName}
    >
      <TooltipProvider>
        <CanvasToolbarShell orientation={orientation} aria-label="Canvas tools">
          {/* Capture verbs — the two content containers, unbundled: click
              places. Design-system grouping: [Note · Doc] first. */}
          <CanvasToolbarGroup
            orientation={orientation}
            data-onboarding-target="canvas-object-tools"
          >
            <CanvasToolbarButton
              label={
                <>
                  {isPlacingTextNote ? "Click to place note" : "Note"}{" "}
                  <ShortcutChip>
                    {CANVAS_KEYS.addNote.toUpperCase()}
                  </ShortcutChip>
                </>
              }
              tooltipSide={tooltipSide}
              active={isPlacingTextNote}
              aria-label={isPlacingTextNote ? "Cancel note" : "Add note"}
              onClick={(e) => {
                closeAll();
                onCancelPlacement?.();
                onToggleTextNoteMode({ x: e.clientX, y: e.clientY });
              }}
            >
              <StickyNote className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>

            <CanvasToolbarButton
              label={
                <>
                  {isPlacingDoc ? "Click to place doc" : "Doc"}{" "}
                  <ShortcutChip>
                    {CANVAS_KEYS.addDoc.toUpperCase()}
                  </ShortcutChip>
                </>
              }
              tooltipSide={tooltipSide}
              active={isPlacingDoc}
              aria-label={isPlacingDoc ? "Cancel doc" : "Add doc"}
              onClick={(e) => {
                closeAll();
                onCancelPlacement?.();
                onToggleDocMode({ x: e.clientX, y: e.clientY });
              }}
            >
              <FileText className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>
          </CanvasToolbarGroup>

          <CanvasToolbarDivider orientation={orientation} />

          {/* Board furniture — structure, annotation, the shape family, then
              the external world. */}
          <CanvasToolbarGroup orientation={orientation}>
            <CanvasToolbarButton
              label={
                <>
                  {isPlacingSection ? "Click to place section" : "Section"}{" "}
                  <ShortcutChip>
                    {CANVAS_KEYS.addSection.toUpperCase()}
                  </ShortcutChip>
                </>
              }
              tooltipSide={tooltipSide}
              active={isPlacingSection}
              aria-label={isPlacingSection ? "Cancel section" : "Add section"}
              onClick={() => {
                closeAll();
                onCancelPlacement?.();
                onToggleSectionMode();
              }}
            >
              <SquareMousePointer className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>

            <CanvasToolbarButton
              label={
                <>
                  {isPlacingTextLabel ? "Click to place label" : "Text · Label"}{" "}
                  <ShortcutChip>
                    {CANVAS_KEYS.addTextLabel.toUpperCase()}
                  </ShortcutChip>
                </>
              }
              tooltipSide={tooltipSide}
              active={labelActive}
              aria-label={
                isPlacingTextLabel ? "Cancel text label" : "Add text label"
              }
              onClick={() => {
                closeAll();
                onCancelPlacement?.();
                onToggleTextLabelMode();
              }}
            >
              <Type className="size-4" strokeWidth={2} />
            </CanvasToolbarButton>

            <CanvasToolbarFlyoutAnchor
              open={isShapeFlyoutOpen}
              onOpenChange={(next) => {
                if (next) {
                  onCancelPlacement?.();
                  closeAll();
                  toggleMenu("shape");
                  onToggleShapesMenu();
                } else {
                  closeAll();
                  onCancelPlacement?.();
                }
              }}
              trigger={
                <CanvasToolbarButton
                  label="Shapes"
                  tooltipSide={tooltipSide}
                  active={shapesActive}
                  aria-expanded={isShapeFlyoutOpen}
                >
                  <ShapesTriggerIcon className="size-4" strokeWidth={2} />
                </CanvasToolbarButton>
              }
            >
              <ShapesPopover
                activeShape={pendingShapeType ?? lastPlacedShape}
                activeConnectorPreset={
                  isConnectorActive ? activeConnectorPreset : null
                }
                onShapeSelect={(shapeType) => {
                  onShapeSelect(shapeType);
                }}
                onConnectorSelect={onConnectorSelect}
                onClose={() => {
                  closeAll();
                  onCancelPlacement?.();
                }}
              />
            </CanvasToolbarFlyoutAnchor>

            <div data-onboarding-target="connections-entry">
              <CanvasConnectorsMenu
                onOpen={() => {
                  closeAll();
                  onCancelPlacement?.();
                  onDismissToolbarMenus?.();
                }}
                onEntitySelect={handleIntegrationEntitySelect}
              />
            </div>
          </CanvasToolbarGroup>

          <CanvasToolbarDivider orientation={orientation} />

          {/* Insert — the comprehensive tail. */}
          <CanvasToolbarGroup orientation={orientation}>
            <CanvasInsertMenu
              variant="toolbar"
              hideIntegrations
              onOpen={() => {
                closeAll();
                onDismissToolbarMenus?.();
              }}
              isWidgetsMenuOpen={isWidgetsMenuOpen}
              isLinkMenuOpen={isLinkMenuOpen}
              onWidgetsMenuOpenChange={(open) => {
                if (open) {
                  closeAll();
                  onCancelPlacement?.();
                  onLinkMenuOpenChange(false);
                }
                onWidgetsMenuOpenChange(open);
              }}
              onLinkMenuOpenChange={(open) => {
                if (open) {
                  closeAll();
                  onCancelPlacement?.();
                  onWidgetsMenuOpenChange(false);
                }
                onLinkMenuOpenChange(open);
              }}
              {...toolbarInsertMenu}
            />
          </CanvasToolbarGroup>
        </CanvasToolbarShell>
      </TooltipProvider>
    </motion.div>
  );
}
